import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation } from "@tanstack/react-query";
import getSiteFileUploadUrl from "../http/getSiteFileUploadUrl";
import postImportInvoice, {
  ImportInvoiceResponse,
  TransferReadingFileRef,
} from "../http/postImportInvoice";
import postSiteBilling, {
  OtherChargeItem,
  SiteBillingRequest,
  SiteBillingResponse,
} from "../http/postSiteBilling";
import putSiteFile from "../http/putSiteFile";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface ProcessBillingPayload {
  siteId: string;
  readingAction: SiteBillingRequest["reading_action"];
  readingDbInsert: boolean;
  readingDbUpdate: boolean;
  attachReadings: boolean;
  invoiceFormat?: "pdf" | "docx";
  invoiceDate?: string; // YYYY-MM-DD
  otherChargeItems?: OtherChargeItem[];
}

interface ProcessImportInvoicePayload {
  siteId: string;
  month: string;
  invoiceFormat?: "pdf" | "docx";
  invoiceDate?: string; // YYYY-MM-DD
  otherChargeItems?: OtherChargeItem[];
}

interface UploadAndProcessTransferReadingsPayload {
  siteId: string;
  month?: string;
  files: File[];
  generateInvoice: boolean;
  invoiceFormat?: "pdf" | "docx";
  invoiceDate?: string; // YYYY-MM-DD
  otherChargeItems?: OtherChargeItem[];
  onUploadProgress?: (current: number, total: number) => void;
}

const useSiteOperationalActions = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const processBilling = useMutation(
    async ({
      siteId,
      readingAction,
      readingDbInsert,
      readingDbUpdate,
      attachReadings,
      invoiceFormat,
      invoiceDate,
      otherChargeItems,
    }: ProcessBillingPayload): Promise<SiteBillingResponse> => {
      return postSiteBilling(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        {
          reading_action: readingAction,
          reading_db_insert: readingDbInsert,
          reading_db_update: readingDbUpdate,
          attach_readings_file: attachReadings,
          invoice_format: invoiceFormat,
          invoice_date: invoiceDate,
          other_charge_items: otherChargeItems,
        }
      );
    }
  );

  const processImportInvoice = useMutation(
    async ({
      siteId,
      month,
      invoiceFormat,
      invoiceDate,
      otherChargeItems,
    }: ProcessImportInvoicePayload): Promise<ImportInvoiceResponse> => {
      return postImportInvoice(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        {
          month,
          invoice_format: invoiceFormat,
          invoice_date: invoiceDate,
          other_charge_items: otherChargeItems,
        }
      );
    }
  );

  const uploadAndProcessTransferReadings = useMutation(
    async ({
      siteId,
      month,
      files,
      generateInvoice,
      invoiceFormat,
      invoiceDate,
      otherChargeItems,
      onUploadProgress,
    }: UploadAndProcessTransferReadingsPayload): Promise<ImportInvoiceResponse> => {
      const authToken = await getAuthTokenBlocking(getToken, relogin);

      // Step 1: Upload all files to S3
      const fileRefs: TransferReadingFileRef[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (onUploadProgress) onUploadProgress(i + 1, files.length);
        const contentType =
          file.type ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const { url, fileGuid } = await getSiteFileUploadUrl(
          authToken,
          siteId,
          "exportReading",
          contentType,
          null,
          file.name
        );
        await putSiteFile(url, contentType, file);
        fileRefs.push({ file_guid: fileGuid, file_name: file.name });
      }

      // Step 2: Trigger async operation with file refs
      return postImportInvoice(authToken, siteId, {
        month,
        files: fileRefs,
        generate_invoice: generateInvoice,
        invoice_format: invoiceFormat,
        invoice_date: invoiceDate,
        other_charge_items: otherChargeItems,
      });
    }
  );

  return {
    processBilling,
    processImportInvoice,
    uploadAndProcessTransferReadings,
  };
};

export default useSiteOperationalActions;
